import { computed, inject, onMounted, ref, toValue, watch } from "vue";
import {
	getSquareCommentList,
	likeSquare,
	likeSquareComment,
	createSquareComment,
	trackSquare,
} from "~/api/api";
import { useShare } from "~/hooks/share/index";
import { useI18n } from "~/composables/useI18n";
import {
	bucketCommentLength,
	buildDetailContentParams,
	hashCommentId,
	resolveDetailMediaType,
	DETAIL_MEDIA_TYPES,
} from "@variants/seer/square-detail/composables/useSquareDetailTracking";

const createReplyState = () => ({
	list: [],
	expanded: false,
	loading: false,
	current_page: 0,
	last_page: 1,
});

const getFollowState = data => data?.is_follow === true || data?.is_follow === 1;
const getZanState = data => data?.is_zan === true || data?.is_zan === 1;

export function useSquare(getSquareData, getCommentSort) {
	const openLoginPopup = inject("openLoginPopup", null);
	const logDetailEvent = inject("logDetailEvent", null);
	const { t, tm, locale } = useI18n();

	const squareData = computed(() => toValue(getSquareData) || {});
	const enableComments = getCommentSort !== undefined;

	// --- 互动：关注、点赞、分享 ---
	const isFollowed = computed(() => getFollowState(squareData.value));
	const isZaned = computed(() => getZanState(squareData.value));
	const squareZanLoading = ref(false);
	const followLoading = ref(false);
	const shareList = ref([]);
	const showSharePanel = ref(false);

	const getDetailMediaType = () => resolveDetailMediaType(squareData.value);

	const trackFollowClick = followStatusBefore => {
		if (!logDetailEvent) return;
		const mediaType = getDetailMediaType();
		const eventName =
			mediaType === DETAIL_MEDIA_TYPES.SHORT
				? "short_author_follow_click"
				: "media_author_follow_click";
		logDetailEvent(
			eventName,
			buildDetailContentParams(squareData.value, {
				media_type: mediaType,
				follow_status_before: followStatusBefore ? "followed" : "unfollowed",
			})
		);
	};

	const trackLikeClick = likeStatusBefore => {
		if (!logDetailEvent) return;
		const mediaType = getDetailMediaType();
		const eventName =
			mediaType === DETAIL_MEDIA_TYPES.SHORT ? "short_like_click" : "media_like_click";
		logDetailEvent(
			eventName,
			buildDetailContentParams(squareData.value, {
				media_type: mediaType,
				like_status_before: likeStatusBefore ? "liked" : "unliked",
			})
		);
	};

	const trackShareClick = shareChannel => {
		if (!logDetailEvent) return;
		const mediaType = getDetailMediaType();
		const eventName =
			mediaType === DETAIL_MEDIA_TYPES.SHORT ? "short_share_click" : "media_share_click";
		logDetailEvent(
			eventName,
			buildDetailContentParams(squareData.value, {
				media_type: mediaType,
				share_channel: String(shareChannel || "unknown")
					.trim()
					.toLowerCase()
					.replace(/\s+/g, "_"),
			})
		);
	};

	const trackCommentSubmit = ({ success, errorCode, commentLength } = {}) => {
		if (!logDetailEvent) return;
		logDetailEvent(
			"media_comment_submit",
			buildDetailContentParams(squareData.value, {
				media_type: getDetailMediaType(),
				comment_length_bucket: bucketCommentLength(commentLength),
				result: success ? "success" : "fail",
				error_code: success ? undefined : errorCode,
			})
		);
	};

	const trackCommentActionClick = ({ actionType, commentId } = {}) => {
		if (!logDetailEvent) return;
		logDetailEvent(
			"media_comment_action_click",
			buildDetailContentParams(squareData.value, {
				media_type: getDetailMediaType(),
				action_type: actionType,
				comment_id_hash: hashCommentId(commentId),
			})
		);
	};

	const getRandomShareText = () => {
		const texts = tm("square.detail.shareTexts");
		if (Array.isArray(texts) && texts.length) {
			return texts[Math.floor(Math.random() * texts.length)];
		}
		return "";
	};

	const updateShareList = () => {
		const newId = squareData.value?.id;
		if (!newId) return;

		const link = import.meta.client ? window.location.href : `/seer/square-detail/${newId}`;

		shareList.value = useShare({
			link,
			text: getRandomShareText(),
			img: squareData.value?.cover,
		});
	};

	watch(() => squareData.value?.id, updateShareList, { immediate: true });

	onMounted(updateShareList);

	const checkLogin = () => {
		if (!import.meta.client) return false;
		if (!localStorage.getItem("token")) {
			showToast(t("square.detail.loginRequired"));
			openLoginPopup?.();
			return false;
		}
		return true;
	};

	const handleFollow = async () => {
		if (!import.meta.client || followLoading.value) return;
		if (!checkLogin()) return;

		const uid = squareData.value?.uid || squareData.value?.publisher_info?.uid;
		if (!uid) return;

		followLoading.value = true;
		const prevFollowed = isFollowed.value;
		trackFollowClick(prevFollowed);
		if (squareData.value) {
			squareData.value.is_follow = !prevFollowed;
		}

		try {
			const res = await trackSquare({ uid: String(uid) });
			const result = res?.data?.[0];
			if (result && squareData.value) {
				squareData.value.is_follow = getFollowState(result);
			}
		} catch (error) {
			if (squareData.value) {
				squareData.value.is_follow = prevFollowed;
			}
			console.error("追蹤失败:", error);
			showToast(t("square.detail.actionFailed"));
		} finally {
			followLoading.value = false;
		}
	};

	const handleZan = async () => {
		if (!import.meta.client || !squareData.value?.id || squareZanLoading.value) return;
		if (!checkLogin()) return;

		squareZanLoading.value = true;
		const prevZaned = isZaned.value;
		trackLikeClick(prevZaned);
		if (squareData.value) {
			squareData.value.is_zan = !prevZaned;
		}

		try {
			await likeSquare({ square_id: String(squareData.value.id) });
		} catch (error) {
			if (squareData.value) {
				squareData.value.is_zan = prevZaned;
			}
			console.error("点赞失败:", error);
			showToast(t("square.detail.actionFailed"));
		} finally {
			squareZanLoading.value = false;
		}
	};

	const handleShare = item => {
		trackShareClick(item?.text);
		if (item.shareFn) {
			item.shareFn();
		}
	};

	const openSharePanel = () => {
		showSharePanel.value = true;
	};

	// --- 评论 ---
	const commentList = ref([]);
	const commentLoading = ref(false);
	const parentCommentPage = ref(1);
	const replyStateMap = ref({});
	const mainCommentText = ref("");
	const activeReplyId = ref(null);
	const activeReplyDraft = ref("");
	const commentSubmitting = ref(false);
	const replySubmitting = ref(false);
	const commentZanLoadingMap = ref({});
	const commentPager = ref({
		current_page: 0,
		last_page: 1,
		total_count: 0,
		reply_count: 0,
	});

	const commentSort = computed(() => (enableComments ? toValue(getCommentSort) || null : null));

	const getSortParams = () => {
		if (commentSort.value === "hot") return { hot: "1" };
		if (commentSort.value === "new") return { new: "1" };
		return {};
	};

	const commentTotal = computed(
		() => commentPager.value.reply_count ?? squareData.value?.reply_num ?? 0
	);

	const hasMoreComments = computed(() => {
		const { current_page, last_page, total_count } = commentPager.value;
		if (current_page < last_page) return true;
		return total_count > commentList.value.length;
	});

	const getReplyState = commentId => replyStateMap.value[commentId] || createReplyState();

	const hasMoreReplies = commentId => {
		const state = getReplyState(commentId);
		return state.expanded && state.current_page < state.last_page;
	};

	const fetchChildReplies = async (parentItem, page = 1, append = false) => {
		if (!squareData.value?.id || !parentItem?.id) return;

		const commentId = String(parentItem.id);
		const prevState = getReplyState(commentId);
		replyStateMap.value = {
			...replyStateMap.value,
			[commentId]: { ...prevState, loading: true },
		};

		try {
			const res = await getSquareCommentList({
				square_id: String(squareData.value.id),
				pid: commentId,
				page,
			});

			const data = res?.data || {};
			const list = Array.isArray(data.list) ? data.list : [];
			const pager = data.pager || {};
			const currentState = getReplyState(commentId);

			replyStateMap.value = {
				...replyStateMap.value,
				[commentId]: {
					list: append ? [...currentState.list, ...list] : list,
					expanded: true,
					loading: false,
					current_page: Number(pager.current_page) || page,
					last_page: Number(pager.last_page) || 1,
				},
			};
		} catch (error) {
			console.error("获取子评论失败:", error);
			replyStateMap.value = {
				...replyStateMap.value,
				[commentId]: { ...getReplyState(commentId), loading: false },
			};
		}
	};

	const toggleViewReplies = async item => {
		const commentId = String(item.id);
		const state = getReplyState(commentId);

		if (state.expanded) {
			replyStateMap.value = {
				...replyStateMap.value,
				[commentId]: { ...state, expanded: false },
			};
			return;
		}

		if (state.list.length) {
			replyStateMap.value = {
				...replyStateMap.value,
				[commentId]: { ...state, expanded: true },
			};
			return;
		}

		await fetchChildReplies(item, 1, false);
	};

	const loadMoreReplies = item => {
		const state = getReplyState(item.id);
		if (state.loading || state.current_page >= state.last_page) return;
		fetchChildReplies(item, state.current_page + 1, true);
	};

	const fetchComments = async (page = 1, append = false) => {
		if (!enableComments || !squareData.value?.id || commentLoading.value) return;

		if (!append) {
			replyStateMap.value = {};
			parentCommentPage.value = 1;
		}

		commentLoading.value = true;
		try {
			const res = await getSquareCommentList({
				square_id: String(squareData.value.id),
				page,
				...getSortParams(),
			});

			const data = res?.data || {};
			const list = Array.isArray(data.list) ? data.list : [];
			const pager = data.pager || {};

			commentList.value = append ? [...commentList.value, ...list] : list;
			parentCommentPage.value = Number(pager.current_page) || page;
			commentPager.value = {
				current_page: parentCommentPage.value,
				last_page: Number(pager.last_page) || 1,
				total_count: Number(pager.total_count) || 0,
				reply_count: Number(pager.reply_count) || 0,
			};
		} catch (error) {
			console.error("获取评论失败:", error);
			if (!append) {
				commentList.value = [];
				parentCommentPage.value = 1;
				commentPager.value = { current_page: 0, last_page: 1, total_count: 0, reply_count: 0 };
			}
		} finally {
			commentLoading.value = false;
		}
	};

	const loadMoreComments = () => {
		if (!hasMoreComments.value || commentLoading.value) return;
		fetchComments(parentCommentPage.value + 1, true);
	};

	const refreshCommentPager = async () => {
		if (!enableComments || !squareData.value?.id) return;

		try {
			const res = await getSquareCommentList({
				square_id: String(squareData.value.id),
				page: 1,
				...getSortParams(),
			});
			const pager = res?.data?.pager || {};

			commentPager.value = {
				...commentPager.value,
				reply_count: Number(pager.reply_count) || 0,
				total_count: Number(pager.total_count) || 0,
				last_page: Number(pager.last_page) || 1,
			};
		} catch (error) {
			console.error("刷新评论计数失败:", error);
		}
	};

	const toggleReplyInput = item => {
		if (!checkLogin()) return;

		const commentId = String(item.id);
		if (String(activeReplyId.value) === commentId) {
			activeReplyId.value = null;
			activeReplyDraft.value = "";
			return;
		}

		activeReplyId.value = commentId;
		activeReplyDraft.value = "";
		trackCommentActionClick({ actionType: "reply", commentId: item.id });
	};

	const submitMainComment = async () => {
		if (!enableComments || !import.meta.client || !squareData.value?.id || commentSubmitting.value)
			return;
		if (!checkLogin()) return;

		const reply = mainCommentText.value.trim();
		if (!reply) return;

		commentSubmitting.value = true;
		try {
			await createSquareComment({
				square_id: String(squareData.value.id),
				reply,
			});

			mainCommentText.value = "";
			await fetchComments(1, false);
			trackCommentSubmit({ success: true, commentLength: reply.length });
			showToast(t("square.detail.commentSuccess"));
		} catch (error) {
			console.error("发表评论失败:", error);
			trackCommentSubmit({
				success: false,
				errorCode: error?.code ?? "unknown",
				commentLength: reply.length,
			});
			showToast(t("square.detail.commentFailed"));
		} finally {
			commentSubmitting.value = false;
		}
	};

	const submitReplyComment = async item => {
		if (
			!enableComments ||
			!import.meta.client ||
			!squareData.value?.id ||
			!item?.id ||
			replySubmitting.value
		)
			return;
		if (!checkLogin()) return;

		const reply = activeReplyDraft.value.trim();
		if (!reply) return;

		replySubmitting.value = true;
		try {
			await createSquareComment({
				square_id: String(squareData.value.id),
				reply_id: String(item.id),
				reply,
			});

			activeReplyId.value = null;
			activeReplyDraft.value = "";

			await refreshCommentPager();

			commentList.value = commentList.value.map(comment =>
				String(comment.id) === String(item.id)
					? { ...comment, reply_count: (Number(comment.reply_count) || 0) + 1 }
					: comment
			);

			// 回复成功后展开并刷新子评论，避免需手动点击「查看回复」才能看到刚发的内容
			await fetchChildReplies(item, 1, false);

			trackCommentSubmit({ success: true, commentLength: reply.length });
			showToast(t("square.detail.replySuccess"));
		} catch (error) {
			console.error("发表回复失败:", error);
			trackCommentSubmit({
				success: false,
				errorCode: error?.code ?? "unknown",
				commentLength: reply.length,
			});
			showToast(t("square.detail.replyFailed"));
		} finally {
			replySubmitting.value = false;
		}
	};

	const isCommentZanLoading = commentId => !!commentZanLoadingMap.value[String(commentId)];

	const updateCommentInLists = (commentId, updater) => {
		const targetId = String(commentId);

		commentList.value = commentList.value.map(item =>
			String(item.id) === targetId ? updater({ ...item }) : item
		);

		const nextMap = { ...replyStateMap.value };
		let mapChanged = false;

		for (const parentId of Object.keys(nextMap)) {
			const state = nextMap[parentId];
			if (!state.list?.length) continue;

			const list = state.list.map(reply =>
				String(reply.id) === targetId ? updater({ ...reply }) : reply
			);

			if (list !== state.list) {
				nextMap[parentId] = { ...state, list };
				mapChanged = true;
			}
		}

		if (mapChanged) {
			replyStateMap.value = nextMap;
		}
	};

	const handleCommentZan = async comment => {
		if (!enableComments || !import.meta.client || !comment?.id) return;

		const commentId = String(comment.id);
		if (isCommentZanLoading(commentId)) return;
		if (!checkLogin()) return;

		commentZanLoadingMap.value = { ...commentZanLoadingMap.value, [commentId]: true };

		const prevZaned = !!comment.is_zan;
		const prevZan = Number(comment.zan) || 0;

		trackCommentActionClick({ actionType: "like", commentId: comment.id });

		updateCommentInLists(commentId, item => ({
			...item,
			is_zan: !prevZaned,
			zan: prevZaned ? Math.max(0, prevZan - 1) : prevZan + 1,
		}));

		try {
			await likeSquareComment({ reply_id: commentId });
		} catch (error) {
			updateCommentInLists(commentId, item => ({
				...item,
				is_zan: prevZaned,
				zan: prevZan,
			}));
			console.error("评论点赞失败:", error);
			showToast(t("square.detail.actionFailed"));
		} finally {
			const nextLoadingMap = { ...commentZanLoadingMap.value };
			delete nextLoadingMap[commentId];
			commentZanLoadingMap.value = nextLoadingMap;
		}
	};

	const resetCommentDraft = () => {
		mainCommentText.value = "";
		activeReplyId.value = null;
		activeReplyDraft.value = "";
	};

	if (enableComments) {
		watch(
			() => squareData.value?.id,
			id => {
				if (id) {
					resetCommentDraft();
					fetchComments(1, false);
				}
			},
			{ immediate: true }
		);

		watch(locale, () => {
			if (!squareData.value?.id) return;
			resetCommentDraft();
			fetchComments(1, false);
		});

		watch(commentSort, (newVal, oldVal) => {
			if (!squareData.value?.id || !newVal) return;
			if (oldVal == null) return;
			resetCommentDraft();
			fetchComments(1, false);
		});
	}

	return {
		isFollowed,
		isZaned,
		squareZanLoading,
		shareList,
		showSharePanel,
		handleFollow,
		handleZan,
		handleShare,
		openSharePanel,
		commentList,
		commentLoading,
		commentTotal,
		hasMoreComments,
		mainCommentText,
		activeReplyId,
		activeReplyDraft,
		commentSubmitting,
		replySubmitting,
		getReplyState,
		hasMoreReplies,
		toggleViewReplies,
		loadMoreReplies,
		loadMoreComments,
		toggleReplyInput,
		submitMainComment,
		submitReplyComment,
		handleCommentZan,
		isCommentZanLoading,
		fetchComments,
	};
}
